import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { ApplicationContext, useAppSetting, useMbox } from "@swa-ui/application";
import { AuthContext, useUserInfo } from "@swa-ui/authentication";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { openDeepLink, useDeviceInfo, useHistory } from "@swa-ui/browser";
import { Button, Icon, InformationTip, Link, PageGrid, Text } from "@swa-ui/core";
import { dateFormats, swaDate } from "@swa-ui/date";
import {
  fireDestinationInfoIconClickAnalytics,
  fireDestinationToolTipSelectAnalytics,
} from "@swa-ui/dotcom-analytics";
import {
  CalendarFormField,
  CheckboxFormField,
  Form,
  RadioButtonFormField,
  RecentSearchContext,
  RequiredIndicator,
  SubmitFormField,
  TextFormField,
  useForm,
  useRecentSearches,
  yupResolver,
} from "@swa-ui/form";
import { useGeolocation } from "@swa-ui/geolocation";
import { useSearchParams } from "@swa-ui/href";
import { AutocompleteStationsFormField, stationMethods } from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";
import { usePersistedState } from "@swa-ui/persistence";
import { InformationTipAdapter, PlacementFactory } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { INT_FLIGHT_BOOKING_WIDGET, INT_LFC_BOOKING_WIDGET } from "../../defines/intParameters";
import {
  ADULTS,
  LAP_CHILDREN,
  OLDER_CHILD,
  TEENS,
  YOUNGER_CHILD,
} from "../../defines/passengerType";
import { SEARCH_TYPE_AIR } from "../../defines/searchType";
import { MULTICITY, ONEWAY, ROUNDTRIP } from "../../defines/tripType";
import { flightBookingFormSchema } from "../../schema/flightBookingFormSchema";
import { SwaBizToggle } from "../../SwaBizToggle/SwaBizToggle";
import { PassengerSelectorFormField } from "../PassengerSelectorFormField";
import { RecentSearchFormField } from "../RecentSearchFormField";
import { SelectListFormField } from "../SelectListFormField";
import styles from "./FlightBookingForm.module.scss";
import {
  filterRecentSearchList,
  getAirBookingSearchHref,
  getAreaStationName,
  getDefaultDate,
  getStationAreaName,
  getStationValue,
  parseMultiStationField,
  useDefaultFormValuesFromStorage,
} from "./utilities";

const WAIT_FOR_CALENDAR_TO_CLOSE = 100;

/**
 * FlightBookingForm displays the necessary fields for a user to begin a search for all trip types. It allows
 * users to select their departure airport, arrival airport, fare type, passengers selection, and their preferred date of travel.
 */
export const FlightBookingForm = (props) => {
  const {
    defaultFormDataOverride,
    disableSearchParams = false,
    enableInternalNavigation = false,
    hideLowFareCalendarCheckbox,
    showCashPlusPointsOption = false,
    showNearbyAirports = true,
    showSwaBizToggle,
  } = props || {};
  const { isLargeOrXlarge, isSmallOrMedium } = useDeviceInfo();
  const maxPassengersCount = useAppSetting("maxPassengersCount", 9);
  const enableCashPlusPointsToggle = useAppSetting("enableCashPlusPoints");
  const { mbox: cashPlusPointsDisplay = "hide" } = useMbox("CashPlusPointsOptionDisplay");
  const searchParamsFromURL = useSearchParams() || {};
  const defaultFormData = getDefaultFormData();
  const defaultFormValuesFromStorage = useDefaultFormValuesFromStorage();
  const { getInternationalStationCode, getStationName } = stationMethods;
  const { addRecentSearch } = useContext(RecentSearchContext);
  const { isLoggedIn, loadingTokenExchange, setPrepareTokenDowngradeOnUnload } =
    useContext(AuthContext);
  const isUserLoggedIn = isLoggedIn();
  const history = useHistory();
  const { recentSearches } = useRecentSearches({ filterByTypes: [SEARCH_TYPE_AIR] });
  const { site } = useContext(ApplicationContext);
  const { userInfoLoading, userInfo } = useUserInfo();
  const [formData, setFormData] = usePersistedState(defaultFormData);
  const [, setAirBookingRedirectSearchErrors] = usePersistedState({
    defaultValue: {},
    key: "airBookingRedirectSearchErrors",
    storageType: "session",
    timeToLiveInMinutes: 2,
  });
  const {
    adultPassengersCount,
    departureDate,
    destinationAirportCode,
    fareType,
    showFareInPoints,
    lowFareCalendar,
    originationAirportCode,
    passengers,
    promoCode,
    returnAirportCode,
    returnDate,
    tripType,
  } = formData;
  const [calendarSize, setCalendarSize] = useState("");
  const [justClosedCalendar, setJustClosedCalendar] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [currentPassengers, setCurrentPassengers] = useState(getInitialPassengers());
  const [readyToNavigateToSearch, setReadyToNavigateToSearch] = useState(false);
  const methods = useForm({ resolver: yupResolver(flightBookingFormSchema()) });
  const {
    formState: { errors },
    setFocus,
    setValue,
    trigger,
  } = methods;
  const airDatesData = getBootstrapData("air/air-dates-data");
  const formattedCurrentLastDateString = swaDate(airDatesData.currentLastBookableDate).format(
    i18n("FlightBookingForm__FULL_MONTH_DAY_YEAR")
  );
  const formattedFutureCloseDateString = swaDate(airDatesData.futureBookingCloseDate).format(
    i18n("FlightBookingForm__FULL_MONTH_DAY_YEAR")
  );
  const formattedFutureOpenDateString = swaDate(airDatesData.futureBookingOpenDate).format(
    i18n("FlightBookingForm__FULL_MONTH_DAY_YEAR")
  );
  const urls = getBootstrapData("urls");
  const ref = useRef(null);
  const returnDateRef = useRef();
  const displaySwaBizToggle = isUserLoggedIn && showSwaBizToggle;
  const internationalStationCodes = useMemo(() => getInternationalStationCode(), []);
  const searchesToTransform = useMemo(
    () => (!showNearbyAirports ? filterRecentSearchList(recentSearches) : recentSearches),
    [recentSearches, showNearbyAirports]
  );

  useEffect(() => {
    const fieldsToUpdate = {
      adultPassengersCount,
      departureDate,
      destinationAirportCode,
      lowFareCalendar,
      originationAirportCode,
      promoCode,
      returnAirportCode,
      returnDate,
      showFareInPoints,
      tripType,
      ...defaultFormValuesFromStorage.formData,
    };

    // Remove nearby airports values for LFC (not supported)
    if (!showNearbyAirports) {
      if (Array.isArray(fieldsToUpdate.originationAirportCode)) {
        fieldsToUpdate.originationAirportCode = "";
      }

      if (Array.isArray(fieldsToUpdate.destinationAirportCode)) {
        fieldsToUpdate.destinationAirportCode = "";
      }
    }

    updateFormData(fieldsToUpdate);

    if (defaultFormValuesFromStorage.passengerData) {
      handlePassengerSelectorChange([defaultFormValuesFromStorage.passengerData]);
    }

    Object.entries(fieldsToUpdate)
      .filter(([, value]) => value && value !== "")
      .forEach(([field, value]) => {
        setValue(field, value);
      });

    const fieldsToTrigger = [
      "adultPassengersCount",
      "departureDate",
      "destinationAirportCode",
      "originationAirportCode",
      "promoCode",
      "returnAirportCode",
      "returnDate",
    ].filter((field) => fieldsToUpdate[field] && fieldsToUpdate[field] !== "");

    if (fieldsToTrigger.length > 0) {
      trigger(fieldsToTrigger);
    }
  }, []);

  useEffect(() => {
    if (readyToNavigateToSearch) {
      const newFormData = {
        ...formData,
        chaseCardValue: userInfo?.["customers.UserInformation.chaseCardInfo.value"],
        destinationAirportCode: getStationValue(destinationAirportCode),
        int: lowFareCalendar ? INT_LFC_BOOKING_WIDGET : INT_FLIGHT_BOOKING_WIDGET,
        lowFareCalendar: formData.lowFareCalendar,
        originationAirportCode: getStationValue(originationAirportCode),
        passengerTier: userInfo?.["customers.UserInformation.tier"],
        promoCode: promoCode?.trim(),
        returnAirportCode: tripType === MULTICITY && returnAirportCode ? returnAirportCode : "",
        returnDate: tripType !== ONEWAY ? returnDate : "",
        searchParams: defaultFormDataOverride?.searchParams ?? {},
        tripType: tripType === MULTICITY && !returnAirportCode ? ROUNDTRIP : tripType,
      };
      const href = getAirBookingSearchHref(newFormData, site, defaultFormDataOverride);

      addRecentSearch(newFormData, SEARCH_TYPE_AIR);

      if (enableInternalNavigation) {
        history.push(href);
      } else {
        openDeepLink({ href });
      }
    }
  }, [readyToNavigateToSearch]);

  return (
    <div className={styles.container}>
      <PageGrid {...getClass()}>
        <Form methods={methods} onSubmit={handleSubmit}>
          {renderAcceptingReservations()}
          <RequiredIndicator className={styles.requiredIndicator} />
          <SelectListFormField {...getSelectListProps()} />
          <RecentSearchFormField {...getRecentSearchProps()} />
          {isMulticity() ? renderMulticityLayout() : renderDefaultLayout()}
          <PassengerSelectorFormField {...getPassengerSelectorFormFieldProps()} />
          <TextFormField {...getPromoCodeProps()} />
          {renderSearchCriteria()}
          {displaySwaBizToggle && (
            <div className={styles.swaBizToggle}>
              <SwaBizToggle />
            </div>
          )}
          <SubmitFormField {...getSubmitButtonProps()}>
            {i18n("FlightBookingForm__BUTTON_SEARCH_FLIGHTS")}
          </SubmitFormField>
        </Form>
        {renderAdditionalLinks()}
      </PageGrid>
    </div>
  );

  function renderDefaultLayout() {
    return (
      <>
        <div className={styles.tripStations}>
          <AutocompleteStationsFormField {...getDepartFieldProps()} />
          {renderSwitcheroo()}
          <AutocompleteStationsFormField {...getArriveFieldProps()} />
          <InformationTipAdapter {...getArriveInformationTipProps()} />
        </div>
        <div className={styles.tripInformation}>
          <CalendarFormField {...getDepartDateProps()} />
          {tripType === ROUNDTRIP && <CalendarFormField {...getReturnDateProps()} />}
        </div>
      </>
    );
  }

  function renderPassengerSelectorMessage(passengerType) {
    const passengerTypeText = passengerType.toUpperCase();

    return (
      <>
        <div className={styles.passengersMessageTitle}>
          {i18n(`FlightBookingForm__PASSENGER_SELECTOR_TITLE_${passengerTypeText}`, {
            maxPassengersCount,
          })}
        </div>
        <div className={styles.passengersMessageDescription}>
          {i18n(`FlightBookingForm__PASSENGER_SELECTOR_${passengerTypeText}_INFO`, {
            maxPassengersCount,
          })}
          <Link {...getPassengerSelectorLinkProps(passengerType, passengerTypeText)}>
            {i18n(`FlightBookingForm__PASSENGER_SELECTOR_LINK_${passengerTypeText}`)}
          </Link>
        </div>
      </>
    );
  }

  function renderMulticityLayout() {
    return (
      <>
        <AutocompleteStationsFormField {...getDepartFieldProps()} />
        <CalendarFormField {...getDepartDateProps()} />
        <AutocompleteStationsFormField {...getArriveFieldProps()} />
        <CalendarFormField {...getReturnDateProps()} />
        <AutocompleteStationsFormField {...getDestinationFieldProps()} />
      </>
    );
  }

  function renderSwitcheroo() {
    return (
      <Button {...getSwitcherooButtonProps()}>
        <Icon {...getIconProps()} />
      </Button>
    );
  }

  function renderCalendarAdjunctContent() {
    return (
      <>
        <div className={styles.lastBookableDate}>
          {i18n("FlightBookingForm__INFO_LAST_BOOKABLE_DATE", { formattedCurrentLastDateString })}
        </div>
        <div className={styles.bookableScheduleExtension}>
          {renderBookableScheduleExtensionContent()}
        </div>
      </>
    );
  }

  function renderAcceptingReservations() {
    return (
      <div className={styles.acceptingReservations}>
        <Text size="small">
          {i18n("FlightBookingForm__INFO_ACCEPTING_RESERVATIONS", {
            formattedCurrentLastDateString,
          })}
          <InformationTip {...getAcceptingReservationsInformationTipProps()} />
        </Text>
      </div>
    );
  }

  function renderAcceptingReservationsInformationTipContent() {
    return (
      <div className={styles.acceptingReservationsInformationTipContent}>
        {renderBookableScheduleExtensionContent()}
      </div>
    );
  }

  function renderAdditionalSearchCriteria() {
    return (
      <div className={styles.fareOptionsContainer}>
        <CheckboxFormField {...getShowFareInPointsProps()} />
        <CheckboxFormField {...getLowFareCalendarProps()} />
      </div>
    );
  }

  function renderBookableScheduleExtensionContent() {
    return (
      <>
        {i18n("FlightBookingForm__INFO_OPEN_SCHEDULE", {
          formattedFutureOpenDateString,
          formattedFutureCloseDateString,
        })}
        {i18n("FlightBookingForm__SUBJECT_TO_CHANGE_TEXT")}
      </>
    );
  }

  function renderAdditionalLinks() {
    return (
      <div className={styles.linkContainer}>
        {isUserLoggedIn && !showSwaBizToggle && renderSwabizLink()}
        <Link className={styles.link} href={urls.AIR_BAGGAGE_AND_OPTIONAL_FEES}>
          {i18n("FlightBookingForm__LINK_BAGGAGE_AND_OPTIONAL_FEES")}
        </Link>
      </div>
    );
  }

  function renderCriteriaWithCashPlusPoints() {
    return (
      <div className={styles.fareOptionsContainer}>
        <RadioButtonFormField {...getCashPlusPointsProps()} />
      </div>
    );
  }

  function renderSearchCriteria() {
    let searchCriteria;

    if (shouldDisplayCashPlusPoints()) {
      searchCriteria = (
        <>
          {renderCriteriaWithCashPlusPoints()}
          {renderShowLowFare()}
        </>
      );
    } else {
      searchCriteria = renderAdditionalSearchCriteria();
    }

    return searchCriteria;
  }

  function renderShowLowFare() {
    return (
      <div className={styles.showLowFareContainer}>
        <CheckboxFormField {...getLowFareCalendarProps()} />
      </div>
    );
  }

  function renderSwabizLink() {
    return (
      <Link
        className={styles.link}
        href={
          isLargeOrXlarge
            ? urls.AIR_BUSINESS_GROUPS_CORPORATE_TRAVEL_HOME_PAGE
            : urls.SWABIZ_BOOKING_HOME_PAGE
        }
      >
        {i18n("FlightBookingForm__LINK_BOOK_WITH_SWABIZ")}
      </Link>
    );
  }

  function getDefaultFormData() {
    if (searchParamsFromURL?.searchParams?.destinationAirportCode) {
      searchParamsFromURL.searchParams.destinationAirportCode = parseMultiStationField(
        searchParamsFromURL?.searchParams?.to,
        searchParamsFromURL?.searchParams?.destinationAirportCode
      );
    }

    if (searchParamsFromURL?.searchParams?.originationAirportCode) {
      searchParamsFromURL.searchParams.originationAirportCode = parseMultiStationField(
        searchParamsFromURL?.searchParams?.from,
        searchParamsFromURL?.searchParams?.originationAirportCode
      );
    }

    const collatedDefaultFormData = {
      ...defaultFormDataOverride,
      ...(!disableSearchParams && searchParamsFromURL.searchParams),
    };
    const {
      adultPassengersCount: adultPassengersCountOverride,
      adultsCount,
      departureDate: departureDateOverride,
      destinationAirportCode: destinationAirportCodeOverride,
      fareType: fareTypeOverride,
      lapInfantPassengersCount,
      olderChildCount,
      originationAirportCode: originationAirportCodeOverride,
      promoCode: promoCodeOverride,
      returnAirportCode: returnAirportCodeOverride,
      returnDate: returnDateOverride,
      teensCount,
      tripType: tripTypeOverride,
      useLowFareCalendar: useLowFareCalendarOverride,
      youngerChildCount,
    } = collatedDefaultFormData;

    return {
      defaultValue: {
        adultPassengersCount: Number(adultPassengersCountOverride) || 1,
        departureDate: departureDateOverride
          ? swaDate(departureDateOverride).format(dateFormats.default)
          : getDefaultDate("departureDate", dateFormats.default),
        destinationAirportCode: destinationAirportCodeOverride ?? "",
        ...(shouldDisplayCashPlusPoints() && { fareType: "USD" }),
        showFareInPoints: fareTypeOverride === "POINTS" ? true : false,
        lowFareCalendar: useLowFareCalendarOverride ?? false,
        originationAirportCode:
          originationAirportCodeOverride ?? useGeolocation()?.nearestStation ?? "",
        passengers: {
          adults: Number(adultsCount) || 1,
          lapChildren: Number(lapInfantPassengersCount) || 0,
          olderChild: Number(olderChildCount) || 0,
          teens: Number(teensCount) || 0,
          youngerChild: Number(youngerChildCount) || 0,
        },
        passengerType: "ADULT",
        promoCode: promoCodeOverride ?? "",
        returnAirportCode: returnAirportCodeOverride ?? "",
        returnDate: returnDateOverride
          ? swaDate(returnDateOverride).format(dateFormats.default)
          : getDefaultDate("returnDate", dateFormats.default),
        tripType: [ONEWAY, ROUNDTRIP, MULTICITY].includes(tripTypeOverride)
          ? tripTypeOverride
          : ROUNDTRIP,
      },
      key: "FlightBookingFormValues",
      timeToLiveInMinutes: 30,
    };
  }

  function getAcceptingReservationsInformationTipProps() {
    return {
      className: styles.informationTipAcceptingReservations,
      content: renderAcceptingReservationsInformationTipContent(),
      infoBoxSize: "micro",
      location: "below",
      size: "small",
      width: "small",
    };
  }

  function getSelectListProps() {
    return {
      className: styles.tripType,
      componentProps: {
        "aria-label": i18n("FlightBookingForm__TRIP_TYPE_ARIA_LABEL"),
        items: [
          {
            displayValue: i18n("FlightBookingForm__ONEWAY_LABEL"),
            label: i18n("FlightBookingForm__ONEWAY_LABEL"),
            value: ONEWAY,
          },
          {
            displayValue: i18n("FlightBookingForm__ROUNDTRIP_LABEL"),
            label: i18n("FlightBookingForm__ROUNDTRIP_LABEL"),
            value: ROUNDTRIP,
          },
          ...(isLargeOrXlarge
            ? [
                {
                  displayValue: i18n("FlightBookingForm__MULTIICTY_LABEL"),
                  label: i18n("FlightBookingForm__MULTIICTY_LABEL"),
                  value: MULTICITY,
                },
              ]
            : []),
        ],
        minimumListWidth: "small",
        onChange: handleTripTypeChange,
        onRevealChange: handleRevealChange,
        revealed,
        ref,
        value: tripType === MULTICITY && isSmallOrMedium ? ROUNDTRIP : tripType,
      },
      disabled: searchInProgress,
      hideBottomSection: true,
      label: i18n("FlightBookingForm__TRIP_TYPE_LABEL"),
      name: "tripType",
    };
  }

  function getRecentSearchProps() {
    return {
      className: styles.recentSearch,
      componentProps: getRecentSearchComponentProps(),
      disabled: searchInProgress,
      hideBottomSection: true,
      label: i18n("FlightBookingForm__RECENT_SEARCHES_LABEL"),
      name: "recentSearch",
    };
  }

  function getRecentSearchComponentProps() {
    const transformedRecentSearches = searchesToTransform.map((recentSearch) => ({
      departureDate: recentSearch.formData.departureDate,
      returnDate:
        recentSearch.formData.tripType !== ONEWAY ? recentSearch.formData.returnDate : null,
      station1: getAreaStationName(recentSearch.formData.originationAirportCode),
      station2: getAreaStationName(recentSearch.formData.destinationAirportCode),
      station3: recentSearch.formData.returnAirportCode
        ? getAreaStationName(recentSearch.formData.returnAirportCode)
        : null,
    }));

    return {
      className: styles.recentSearches,
      list: transformedRecentSearches,
      onChange: handleRecentSearchSelection,
      searchType: SEARCH_TYPE_AIR,
    };
  }

  function getCashPlusPointsProps() {
    return {
      componentProps: {
        ariaLiveInput: (option, index, length) =>
          `${i18n("FlightBookingForm__RADIO_BUTTON_ARIA_LIVE", {
            index,
            length,
            option,
          })}`,
        defaultValue: fareType || "USD",
        direction: "row",
        hideBottomSection: true,
        onChange: (value) => updateFormData({ fareType: value }),
        radioButtonInfoList: [
          {
            label: i18n("FlightBookingForm__FARE_TYPE_CASH"),
            value: "USD",
          },
          {
            label: i18n("FlightBookingForm__FARE_TYPE_POINTS"),
            value: "POINTS",
          },
          {
            label: i18n("FlightBookingForm__FARE_TYPE_CASH_PLUS_POINTS"),
            value: "USD_PLUS_POINTS",
          },
        ],
      },
      label: i18n("FlightBookingForm__SHOW_FARES_IN"),
      labelInformationTipProps: { ...getShowFaresTipProps() },
      name: "fareType",
      value: fareType || "USD",
    };
  }

  function getDepartFieldProps() {
    return {
      caption: originationAirportCode ? getCaption(originationAirportCode) : undefined,
      className: styles.depart,
      componentProps: {
        filteredStations: getFilteredStations(destinationAirportCode),
        invalidRouteText: i18n("FlightBookingForm__INVALID_ROUTE_TEXT"),
        labelText: i18n("FlightBookingForm__DEPART_LABEL"),
        nearbyAirports: !isMulticity() && showNearbyAirports,
        noMatchFoundText: i18n("FlightBookingForm__NO_MATCH_FOUND_TEXT"),
        onChange: (event) => {
          updateFormData({
            originationAirportCode: event.target.value,
            lowFareCalendar: getLowfareCalendarUpdatedValue(event, destinationAirportCode),
          });
        },
        required: true,
        value: originationAirportCode,
      },
      disabled: searchInProgress,
      label: i18n("FlightBookingForm__DEPART_LABEL"),
      name: "originationAirportCode",
    };
  }

  function getSwitcherooButtonProps() {
    return {
      className: styles.switcherooButton,
      disabled: searchInProgress,
      onClick: handleSwitcherooButtonClick,
      styleType: "no-style",
    };
  }

  function getIconProps() {
    return {
      "aria-label": i18n("FlightBookingForm__SWITCHEROO_BUTTON_ARIA_LABEL"),
      background: {
        color: "flight-booking-form-switcheroo-button-background",
        type: "circle",
      },
      disabled: searchInProgress,
      name: "ChangeFlight",
      shrink: 0.75,
      size: "size30",
      transparentBorder: false,
    };
  }

  function getArriveInformationTipProps() {
    return {
      className: styles.informationTipArrive,
      content: (
        <PlacementFactory
          id="destinationToolTip"
          additionalProps={{
            templateData: { handleDestinationToolTipSelect },
          }}
        />
      ),
      infoBoxSize: "micro",
      size: "small",
      width: "small",
      onClick: handleClick,
    };
  }

  function getShowFaresTipProps() {
    return {
      className: styles.informationTipShowFares,
      content: <PlacementFactory id="tooltipPriceFormat" />,
      location: "below",
      size: "small",
      width: "small",
    };
  }

  function getArriveFieldProps() {
    const label = isMulticity()
      ? i18n("FlightBookingForm__DESTINATION_CITY_1_LABEL")
      : i18n("FlightBookingForm__ARRIVE_LABEL");

    return {
      caption: destinationAirportCode ? getCaption(destinationAirportCode) : undefined,
      className: styles.arrive,
      componentProps: {
        filteredStations: getFilteredStations(originationAirportCode),
        invalidRouteText: i18n("FlightBookingForm__INVALID_ROUTE_TEXT"),
        labelText: label,
        nearbyAirports: !isMulticity() && showNearbyAirports,
        noMatchFoundText: i18n("FlightBookingForm__NO_MATCH_FOUND_TEXT"),
        onChange: (event) => {
          updateFormData({
            destinationAirportCode: event.target.value,
            lowFareCalendar: getLowfareCalendarUpdatedValue(event, originationAirportCode),
          });
        },
        required: true,
        value: destinationAirportCode,
      },
      disabled: searchInProgress,
      label,
      name: "destinationAirportCode",
    };
  }

  function getDestinationFieldProps() {
    return {
      caption: returnAirportCode ? getStationName(returnAirportCode) : undefined,
      className: styles.destinationCity2,
      componentProps: {
        defaultValue: returnAirportCode,
        filteredStations: [destinationAirportCode],
        invalidRouteText: i18n("FlightBookingForm__INVALID_ROUTE_TEXT"),
        labelText: i18n("FlightBookingForm__DESTINATION_CITY_2_LABEL"),
        noMatchFoundText: i18n("FlightBookingForm__NO_MATCH_FOUND_TEXT"),
        nearbyAirports: !isMulticity() && showNearbyAirports,
        onChange: (event) =>
          updateFormData({
            returnAirportCode: event.target.value,
          }),
        required: true,
      },
      disabled: searchInProgress,
      label: i18n("FlightBookingForm__DESTINATION_CITY_2_LABEL"),
      name: "returnAirportCode",
    };
  }

  function getDepartDateProps() {
    return {
      caption: getDateCaption(departureDate),
      className: styles.departureDate,
      componentProps: getCalendarProps(i18n("FlightBookingForm__DEPART_DATE_ARIA_LABEL"), "start"),
      disabled: searchInProgress,
      label: i18n("FlightBookingForm__DEPART_DATE_LABEL"),
      name: "departureDate",
    };
  }

  function getReturnDateProps() {
    const label = isMulticity()
      ? i18n("FlightBookingForm__DEPART_DATE_LABEL")
      : i18n("FlightBookingForm__RETURN_DATE_LABEL");

    return {
      caption: getDateCaption(returnDate),
      className: styles.returnDate,
      componentProps: getCalendarProps(
        isMulticity()
          ? i18n("FlightBookingForm__DEPART_DATE_ARIA_LABEL")
          : i18n("FlightBookingForm__RETURN_DATE_ARIA_LABEL"),
        "end"
      ),
      disabled: searchInProgress,
      label,
      name: "returnDate",
    };
  }

  function getPassengerSelectorFormFieldProps() {
    return {
      className: styles.passengers,
      componentProps: {
        "aria-label": i18n("FlightBookingForm__PASSENGER_SELECTOR_ARIA"),
        groups: [
          {
            items: currentPassengers,
          },
        ],
        maxCount: maxPassengersCount * 2,
        onCancel: handlePassengerSelectorCancel,
        onChange: handlePassengerSelectorChange,
        onUpdate: handlePassengerSelectorUpdate,
      },
      disabled: searchInProgress,
      label: i18n("FlightBookingForm__PASSENGERS_LABEL"),
      name: "adultPassengersCount",
    };
  }

  function getPassengerSelectorLinkProps(passengerType, textType) {
    return {
      "aria-label": i18n(`FlightBookingForm__PASSENGER_SELECTOR_${textType}_ARIA_LABEL`, {
        maxPassengersCount,
      }),
      href: getAdjuctContentURL(passengerType),
      newWindow: true,
      size: "fontSize12",
      styleType: "link",
    };
  }

  function getPromoCodeProps() {
    return {
      className: styles.promoCode,
      componentProps: {
        "aria-label": i18n("FlightBookingForm__PROMO_CODE_ARIA_LABEL"),
        maxLength: 10,
        onChange: (event) => updateFormData({ promoCode: event.target.value }),
        value: promoCode,
      },
      disabled: searchInProgress,
      label: i18n("FlightBookingForm__PROMO_CODE_LABEL"),
      name: "promoCode",
    };
  }

  function getShowFareInPointsProps() {
    return {
      className: styles.showFareInPoints,
      componentProps: {
        defaultValue: showFareInPoints ?? false,
        onChange: (value) => updateFormData({ showFareInPoints: value }),
        label: i18n("FlightBookingForm__SHOW_FARE_IN_POINTS_LABEL"),
      },
      disabled: searchInProgress,
      hideBottomSection: true,
      hideTopSection: true,
      name: "showFareInPoints",
    };
  }

  function getLowFareCalendarProps() {
    return {
      className: hideLowFareCalendarCheckbox
        ? styles.lowFareCalendarCheckboxHidden
        : styles.lowFareCalendarCheckbox,
      componentProps: {
        defaultValue: hasMultipleDestinations() ? false : lowFareCalendar,
        onChange: (value) => updateFormData({ lowFareCalendar: value }),
        label: i18n("FlightBookingForm__SEARCH_LOW_FARE_CALENDAR_LABEL"),
        value: hasMultipleDestinations() ? false : lowFareCalendar,
      },
      disabled: searchInProgress || hasMultipleDestinations(),
      hideBottomSection: true,
      hideTopSection: true,
      name: "lowFareCalendar",
    };
  }

  function getSubmitButtonProps() {
    return {
      className: styles.submit,
      componentProps: {
        fullWidth: true,
        styleType: "primary",
        submitInProgress: searchInProgress,
      },
      disabled: loadingTokenExchange || userInfoLoading,
      hideBottomSection: true,
      id: "flightBookingSubmit",
      name: "submit",
    };
  }

  function getCalendarProps(ariaLabel, focusField) {
    return {
      adjunctContent: renderCalendarAdjunctContent(),
      "aria-label": ariaLabel,
      closeContent: i18n("FlightBookingForm__BUTTON_CLOSE"),
      dateEnd: returnDate,
      dateStart: departureDate,
      daysOfWeekInitials: i18n("FlightBookingForm__DAYS_OF_WEEK_INITIALS"),
      defaultValue: focusField === "end" ? returnDate : departureDate,
      departText: i18n("FlightBookingForm__DEPART_BUTTON_TEXT"),
      focusField,
      format: i18n("FlightBookingForm__SHORT_DATE_MONTH_AND_DAY_FORMAT"),
      lastBookableDate: swaDate(airDatesData.currentLastBookableDate).format(
        i18n("FlightBookingForm__DATE_FORMAT")
      ),
      numberCalendars: 2,
      numberDates: tripType === ONEWAY ? 1 : 2,
      onClose: handleCalendarClose,
      onSelectEnd: handleSelectEnd,
      onSelectStart: handleSelectStart,
      onSetupEnd: handleCalendarSetupEnd,
      openOnFocus: isLargeCalendar() || !justClosedCalendar,
      ref: focusField === "end" && tripType === ROUNDTRIP ? returnDateRef : null,
      required: true,
      returnText: i18n("FlightBookingForm__RETURN_BUTTON_TEXT"),
      selectDateText: i18n("FlightBookingForm__SELECT_DATE_TEXT"),
      shouldLeaveOpen: !isLargeCalendar() && tripType !== ONEWAY,
      title: i18n("FlightBookingForm__TITLE_DATE_SELECTOR"),
    };
  }

  function getClass() {
    return {
      className: classNames({
        [styles.flightWidgetLayout]: true,
        [styles.oneWayLayout]: tripType === ONEWAY,
        [styles.roundTripLayout]:
          tripType === ROUNDTRIP || (tripType === MULTICITY && isSmallOrMedium),
        [styles.multiCityLayout]: isMulticity(),
        [styles.swaBizToggleVisible]: displaySwaBizToggle,
      }),
    };
  }

  function getFilteredStations(airportCode) {
    const defaultFilteredStations = Array.isArray(airportCode) ? airportCode : [airportCode];

    return internationalStationCodes.includes(defaultFilteredStations?.[0])
      ? internationalStationCodes
      : defaultFilteredStations;
  }

  function handleClick(isCollapsed) {
    !isCollapsed && fireDestinationInfoIconClickAnalytics();
  }

  function handleDestinationToolTipSelect(event, destination) {
    fireDestinationToolTipSelectAnalytics(destination);
    updateFormData({ destinationAirportCode: destination });
    setValue("destinationAirportCode", destination);
    setFocus("destinationAirportCode");
  }

  function handlePassengerSelectorCancel(values) {
    setCurrentPassengers(fillIn(values[0]));
  }

  function handlePassengerSelectorChange(values) {
    setCurrentPassengers(fillIn(values[0]));
    setValue("passengers", passengers);
    updatePassengerData(values[0]);
  }

  function handleRecentSearchSelection(index) {
    const recentSearchData = recentSearches[index].formData;
    const passengersOrder = ["adults", "teens", "olderChild", "youngerChild", "lapChildren"];
    const passengerValues = transformPassengerValues(recentSearchData.passengers, passengersOrder);

    updateFormData(recentSearchData);
    setValue("adultPassengersCount", getTotalAdultPassengers(recentSearchData.passengers));
    setValue("departureDate", recentSearchData.departureDate);
    setValue("returnDate", recentSearchData.returnDate);
    setValue("tripType", recentSearchData.tripType);
    setValue("showfareInPoints", recentSearchData.showfareInPoints);
    setValue("promoCode", recentSearchData.promoCode);
    setValue("originationAirportCode", recentSearchData.originationAirportCode);
    setValue("destinationAirportCode", recentSearchData.destinationAirportCode);
    handlePassengerSelectorChange(passengerValues);
    recentSearchData.tripType === MULTICITY &&
      setValue("returnAirportCode", recentSearchData.returnAirportCode);
  }

  function handleRevealChange(open) {
    setRevealed(open);
  }

  function handleSelectEnd(value) {
    const newFormData = { departureDate };
    let selectionError = false;

    if (swaDate(departureDate).isAfter(value, "day")) {
      newFormData.departureDate = value;
      newFormData.returnDate = "";
      setValue("departureDate", value);
      setValue("returnDate", "");
      selectionError = true;
    } else {
      newFormData.returnDate = value;
      setValue("returnDate", value);
    }

    updateFormData(newFormData);

    return selectionError;
  }

  function handleSelectStart(value) {
    const newFormData = { returnDate };

    isLargeCalendar() &&
      setTimeout(() => returnDateRef?.current?.focus(), WAIT_FOR_CALENDAR_TO_CLOSE);

    if (swaDate(value).isAfter(returnDate, "day")) {
      newFormData.returnDate = "";
      setValue("returnDate", "");
    }

    setValue("departureDate", value);
    newFormData.departureDate = value;

    updateFormData(newFormData);
  }

  function handleCalendarSetupEnd(size) {
    setCalendarSize(size);
  }

  function handlePassengerSelectorUpdate(values) {
    setCurrentPassengers(fillIn(values[0]));
  }

  function handleSwitcherooButtonClick() {
    if (originationAirportCode || destinationAirportCode) {
      const [newOriginationAirportCode, newDestinationAirportCode] = [
        destinationAirportCode,
        originationAirportCode,
      ];

      updateFormData({
        destinationAirportCode: newDestinationAirportCode,
        originationAirportCode: newOriginationAirportCode,
      });

      setValue("destinationAirportCode", newDestinationAirportCode);
      setValue("originationAirportCode", newOriginationAirportCode);
      trigger("originationAirportCode");
      trigger("destinationAirportCode");
    }
  }

  function handleTripTypeChange(event) {
    const updatedTripType = event.target.value;

    updateFormData({
      destinationAirportCode: getUpdatedStationValue(destinationAirportCode, updatedTripType),
      originationAirportCode: getUpdatedStationValue(originationAirportCode, updatedTripType),
      tripType: updatedTripType,
    });
    setValue("tripType", updatedTripType);
    setValue(
      "originationAirportCode",
      getUpdatedStationValue(originationAirportCode, updatedTripType)
    );
    setValue(
      "destinationAirportCode",
      getUpdatedStationValue(destinationAirportCode, updatedTripType)
    );
    errors?.returnDate && trigger("returnDate");
    errors?.destinationAirportCode && trigger("destinationAirportCode");
  }

  function handleSubmit() {
    setAirBookingRedirectSearchErrors({});
    setPrepareTokenDowngradeOnUnload(false);
    sessionStorage.removeItem("PurchasePageStore");
    sessionStorage.removeItem("SplitPageSearchResultsCache");
    sessionStorage.removeItem("CorporateProxyBookingStore");
    setSearchInProgress(!searchInProgress);
    setReadyToNavigateToSearch(true);
  }

  function handleCalendarClose() {
    setJustClosedCalendar(true);
    setTimeout(() => {
      setJustClosedCalendar(false);
    }, WAIT_FOR_CALENDAR_TO_CLOSE);
  }

  function fillIn(updatePassengerValues) {
    const dynamicMaxCounts = getCurrentMaxCounts(updatePassengerValues);
    let revealAdjunctContents = getRevealAdjunctContents(updatePassengerValues);

    return currentPassengers.map((item, index) => {
      const { adjunctContent, key, title, titleCaption } = item;

      if (updatePassengerValues[index] > dynamicMaxCounts[key]) {
        updatePassengerValues[index] = dynamicMaxCounts[key];
        revealAdjunctContents = getRevealAdjunctContents(updatePassengerValues);
      }

      return {
        adjunctContent,
        adjunctContentRevealed: revealAdjunctContents[key],
        key,
        maximumValue: dynamicMaxCounts[key],
        title,
        titleCaption,
        value: updatePassengerValues[index],
      };
    });
  }

  function getAdjuctContentURL(passengerType) {
    const { AIR_GROUP_TRAVEL_FAQ, AIR_UNACCOMPANIED_MINOR_TRAVELER_PAGE, LAP_CHILD_POLICY } =
      getBootstrapData("urls");

    const adjuctContentURLs = {
      [ADULTS]: AIR_GROUP_TRAVEL_FAQ,
      [OLDER_CHILD]: AIR_UNACCOMPANIED_MINOR_TRAVELER_PAGE,
      [LAP_CHILDREN]: LAP_CHILD_POLICY,
    };

    return adjuctContentURLs[passengerType];
  }

  function getCaption(currentValue) {
    let result;

    if (Array.isArray(currentValue)) {
      result =
        currentValue.length > 1
          ? isMulticity()
            ? ""
            : getStationAreaName(currentValue[0])
          : getStationName(currentValue[0]);
    } else {
      result = getStationName(currentValue);
    }

    return result;
  }

  function getCurrentPassengerCounts(values) {
    return currentPassengers.reduce((acc, passenger, index) => {
      acc[passenger.key] = values[index];

      return acc;
    }, {});
  }

  function getCurrentMaxCounts(values) {
    return getMaxCounts(getCurrentPassengerCounts(values));
  }

  function getDateCaption(dateString) {
    return dateString
      ? swaDate(dateString).format(i18n("FlightBookingForm__DAY_OF_WEEK_MONTH_AND_YEAR"))
      : "";
  }

  function getInitialPassengers() {
    const currentMaxCounts = getMaxCounts(passengers);

    return [
      {
        adjunctContent: renderPassengerSelectorMessage(ADULTS),
        key: ADULTS,
        maximumValue: currentMaxCounts[ADULTS],
        title: i18n("FlightBookingForm__PASSENGER_SELECTOR_ADULTS_LABEL"),
        titleCaption: i18n("FlightBookingForm__PASSENGER_SELECTOR_ADULTS_CAPTION"),
        value: passengers?.adults,
      },
      {
        key: TEENS,
        maximumValue: currentMaxCounts[TEENS],
        title: i18n("FlightBookingForm__PASSENGER_SELECTOR_TEENS_LABEL"),
        titleCaption: i18n("FlightBookingForm__PASSENGER_SELECTOR_TEENS_CAPTION"),
        value: passengers?.teens,
      },
      {
        adjunctContent: renderPassengerSelectorMessage(OLDER_CHILD),
        key: OLDER_CHILD,
        maximumValue: currentMaxCounts[OLDER_CHILD],
        title: i18n("FlightBookingForm__PASSENGER_SELECTOR_CHILDREN_LABEL"),
        titleCaption: i18n("FlightBookingForm__PASSENGER_SELECTOR_OLDERCHILD_CAPTION"),
        value: passengers?.olderChild,
      },
      {
        key: YOUNGER_CHILD,
        maximumValue: currentMaxCounts[YOUNGER_CHILD],
        title: i18n("FlightBookingForm__PASSENGER_SELECTOR_CHILDREN_LABEL"),
        titleCaption: i18n("FlightBookingForm__PASSENGER_SELECTOR_YOUNGERCHILD_CAPTION"),
        value: passengers?.youngerChild,
      },
      {
        adjunctContent: renderPassengerSelectorMessage(LAP_CHILDREN),
        key: LAP_CHILDREN,
        maximumValue: currentMaxCounts[LAP_CHILDREN],
        title: i18n("FlightBookingForm__PASSENGER_SELECTOR_LAPCHILD_LABEL"),
        titleCaption: i18n("FlightBookingForm__PASSENGER_SELECTOR_LAPCHILD_CAPTION"),
        value: passengers?.lapChildren,
      },
    ];
  }

  function getLowfareCalendarUpdatedValue(event, airportCode) {
    let updatedLowFareCalendarValue = lowFareCalendar;
    const airportCodeArray = Array.isArray(airportCode) ? airportCode : [airportCode];

    if (lowFareCalendar && tripType !== MULTICITY) {
      updatedLowFareCalendarValue = !(
        event.target.value?.length > 1 || airportCodeArray?.length > 1
      );
    }

    return updatedLowFareCalendarValue;
  }

  function getMaxCounts(passengerCounts) {
    const { adults, olderChild, teens, youngerChild } = passengerCounts;

    return {
      [ADULTS]: maxPassengersCount - olderChild - youngerChild - teens,
      [TEENS]: maxPassengersCount - olderChild - adults - youngerChild,
      [OLDER_CHILD]: maxPassengersCount - adults - teens - youngerChild,
      [YOUNGER_CHILD]: maxPassengersCount - adults - teens - olderChild,
      [LAP_CHILDREN]: adults + teens,
    };
  }

  function getRevealAdjunctContents(values) {
    const { adults, olderChild, teens, youngerChild, lapChildren } =
      getCurrentPassengerCounts(values);

    return {
      [ADULTS]: maxPassengersCount === adults + olderChild + youngerChild + teens,
      [OLDER_CHILD]: olderChild > 0 && adults === 0 && teens === 0,
      [LAP_CHILDREN]: lapChildren > 0,
    };
  }

  function getTotalAdultPassengers(passengerCounts) {
    const adultPassengers = { ...passengerCounts };

    delete adultPassengers.lapChildren;

    return Object.keys(adultPassengers)
      .reduce((acc, key) => acc + passengerCounts[key], 0)
      .toString();
  }

  function getUpdatedStationValue(currentValue, updatedTripType) {
    return Array.isArray(currentValue) && updatedTripType === MULTICITY
      ? currentValue.length > 1
        ? ""
        : currentValue.toString()
      : currentValue;
  }

  function hasMultipleDestinations() {
    return (
      tripType !== MULTICITY &&
      ((Array.isArray(originationAirportCode) && originationAirportCode.length > 1) ||
        (Array.isArray(destinationAirportCode) && destinationAirportCode.length > 1))
    );
  }

  // mWeb does not support multicity bookings, disabling multicity for small and medium breakpoints (QUAMOn-936)
  // remove all screen size checks for multicity after responsive booking
  function isMulticity() {
    return tripType === MULTICITY && isLargeOrXlarge;
  }

  function transformPassengerValues(passengerValues, order) {
    const result = order.map((key) => passengerValues[key]);

    return [result];
  }

  function updateFormData(newFormData) {
    setFormData((prevFormData) => ({ ...prevFormData, ...newFormData }));
  }

  function updatePassengerData(value) {
    const newPassengersValue = { ...passengers };

    newPassengersValue.adults = value[0];
    newPassengersValue.teens = value[1];
    newPassengersValue.olderChild = value[2];
    newPassengersValue.youngerChild = value[3];
    newPassengersValue.lapChildren = value[4];

    const newAdultPassengersValue = getTotalAdultPassengers(newPassengersValue);

    updateFormData({
      adultPassengersCount: newAdultPassengersValue,
      passengers: newPassengersValue,
    });

    setValue("adultPassengersCount", newAdultPassengersValue);
    trigger("adultPassengersCount");
  }

  function isLargeCalendar() {
    return calendarSize === "large";
  }

  function shouldDisplayCashPlusPoints() {
    return (
      showCashPlusPointsOption &&
      !isSmallOrMedium &&
      enableCashPlusPointsToggle &&
      cashPlusPointsDisplay === "show"
    );
  }
};

FlightBookingForm.propTypes = {
  /** Consumers of this component can optionally set default values for form fields using this prop.
   *  For example, a static page driven by WCM data could use this prop. */
  defaultFormDataOverride: PropTypes.shape({
    departureDate: PropTypes.string,
    destinationAirportCode: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    fareType: PropTypes.string,
    originationAirportCode: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    promoCode: PropTypes.string,
    returnDate: PropTypes.string,
    searchParams: PropTypes.object,
    tripType: PropTypes.string,
    useLowFareCalendar: PropTypes.bool,
  }),

  /** Set this flag to true if auto population of form fields using URL
   *  query params should be disabled. */
  disableSearchParams: PropTypes.bool,

  /** When true, hides the low fare calendar checkbox option from the form */
  hideLowFareCalendarCheckbox: PropTypes.bool,

  /** Boolean prop passed to AutocompleteStationsFormField. When false, it will not render nearby airport select options  */
  showNearbyAirports: PropTypes.bool,

  /** Shows the 'Book with a SWABIZ account' toggle switch if true; shows the 'Book with SWABIZ' link if false */
  showSwaBizToggle: PropTypes.bool,

  /** When true, uses internal SPA navigation (history.push) instead of external navigation (openDeepLink) */
  enableInternalNavigation: PropTypes.bool,
};
